import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Dream, DreamConfig } from '../../types/dreamSimulator';
import { formatCurrency } from '../../utils/formatters';
import { compoundMultiplierContent } from '../../data/compoundMultiplierContent';
import AnimatedNumberDisplay from '../ui/AnimatedNumberDisplay';
import { Investment } from '../../types';
import { DollarSign, Calendar, Target, ChevronRight, RefreshCw, Plus, Minus, BarChart3 } from 'lucide-react';
import { Toggle } from '../ui/Toggle';
import { optimizeCertificatesForGoal, calculateTotalInvestment, calculateDownPayment, calculateMonthlyPayment } from '../../utils/calculations/dreamSimulatorCalculations';
import ChartSeriesToggleCard from '../ui/ChartSeriesToggleCard';
import { useCalculator } from '../../context/CalculatorContext';
import { calculateResults } from '../../utils/calculations';

interface DreamConfigurationProps {
  dream: Dream;
  config: DreamConfig;
  onConfigUpdate: (config: Partial<DreamConfig>) => void;
  onContinue: () => void;
  investment: Investment;
}

const DreamConfiguration: React.FC<DreamConfigurationProps> = ({ 
  dream, 
  config, 
  onConfigUpdate, 
  onContinue,
  investment
}) => {
  const [isCustomGoal, setIsCustomGoal] = useState(false);
  const [isCustomTimeframe, setIsCustomTimeframe] = useState(false);
  
  const Icon = dream.icon;
  
  // Calculate actual results to get real certificate numbers
  const [actualResults, setActualResults] = useState<any>(null);
  const [actualResultsWithoutReinvestment, setActualResultsWithoutReinvestment] = useState<any>(null);
  
  // Calculate results when config changes
  useEffect(() => {
    try {
      // Calculate results WITH reinvestment
      const testInvestment = {
        ...investment,
        initialCertificates: config.certificatesNeeded,
        years: config.timeframe,
        reinvestProfits: true
      };
      const results = calculateResults(testInvestment);
      setActualResults(results);
      
      // Calculate results WITHOUT reinvestment for comparison
      const testInvestmentWithoutReinvestment = {
        ...investment,
        initialCertificates: config.certificatesNeeded,
        years: config.timeframe,
        reinvestProfits: false
      };
      const resultsWithoutReinvestment = calculateResults(testInvestmentWithoutReinvestment);
      setActualResultsWithoutReinvestment(resultsWithoutReinvestment);
    } catch (error) {
      console.error('Error calculating results:', error);
      setActualResults(null);
      setActualResultsWithoutReinvestment(null);
    }
  }, [config.certificatesNeeded, config.timeframe, investment.averageProductionPerHectare, investment.averageSalePricePerKg]);
  
  // Presets for monthly goals
  const monthlyGoalPresets = [
    { label: 'Básico', value: dream.monthlyGoal * 0.5 },
    { label: 'Estándar', value: dream.monthlyGoal },
    { label: 'Premium', value: dream.monthlyGoal * 1.5 }
  ];
  
  // Presets for timeframes
  const timeframePresets = [
    { label: 'Corto', value: 5 },
    { label: 'Medio', value: 10 },
    { label: 'Largo', value: 25 }
  ];
  
  // Calculate required certificates based on monthly goal and timeframe
  const calculateRequiredCertificates = () => {
    return optimizeCertificatesForGoal(config, investment);
  };
  
  // Update certificates when monthly goal, timeframe, or reinvestment setting changes
  useEffect(() => {
    const requiredCertificates = calculateRequiredCertificates();
    onConfigUpdate({ certificatesNeeded: requiredCertificates });
  }, [config.monthlyGoal, config.timeframe]);
  
  // Handle monthly goal change
  const handleMonthlyGoalChange = (value: number) => {
    onConfigUpdate({ monthlyGoal: value });
  };
  
  // Handle timeframe change
  const handleTimeframeChange = (value: number) => {
    onConfigUpdate({ timeframe: value });
  };
  
  // Calculate investment details
  const totalInvestment = calculateTotalInvestment(config.certificatesNeeded, investment.certificateBasePrice);
  const downPayment = calculateDownPayment(totalInvestment);
  const monthlyPayment = calculateMonthlyPayment(totalInvestment);
  
  // Get real certificate numbers from actual results
  const currentReinvestmentSetting = config.reinvestProfits !== undefined ? config.reinvestProfits : investment.reinvestProfits;
  
  // Always use WITH reinvestment for "Con Multiplicador™" section
  const withMultiplierCertificatesFromReinvestment = actualResults?.certificatesSummary?.fromReinvestment || 0;
  const withMultiplierTotalCertificates = actualResults?.certificatesSummary?.totalCertificates || config.certificatesNeeded;
  
  // Use WITHOUT reinvestment for "Inversión Tradicional" section
  const withoutMultiplierTotalCertificates = config.certificatesNeeded; // Always the same as purchased
  
  const { updateInvestment } = useCalculator();
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <motion.div 
          className="bg-gradient-to-r from-indigo-500 to-purple-600 p-8 text-white"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Icon className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Multiplicador™ - {dream.name}</h2>
              <p className="text-indigo-100">{compoundMultiplierContent.tagline}</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          className="p-8 space-y-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          {/* Production Parameters */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              <span>Parámetros de Producción</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Production per Hectare */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700">Producción Promedio por Hectárea</label>
                <div className="flex items-center justify-center gap-4 mb-3">
                  <button 
                    onClick={() => updateInvestment({ averageProductionPerHectare: Math.max(15000, investment.averageProductionPerHectare - 500) })}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.averageProductionPerHectare <= 15000}
                  >
                    <Minus className="w-5 h-5 text-gray-700" />
                  </button>
                  <div className="text-center">
                    <input
                      type="number"
                      min="15000"
                      max="35000"
                      step="500"
                      value={investment.averageProductionPerHectare}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value >= 15000 && value <= 35000) {
                          updateInvestment({ averageProductionPerHectare: value });
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-24 text-center text-2xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                    />
                    <div className="text-sm text-green-600">
                      kg/hectárea
                    </div>
                  </div>
                  <button 
                    onClick={() => updateInvestment({ averageProductionPerHectare: Math.min(35000, investment.averageProductionPerHectare + 500) })}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.averageProductionPerHectare >= 35000}
                  >
                    <Plus className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
                
                <input
                  type="range"
                  min="15000"
                  max="35000"
                  step="500"
                  value={investment.averageProductionPerHectare}
                  onChange={(e) => updateInvestment({ averageProductionPerHectare: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gradient-to-r from-green-200 to-green-300 rounded-lg appearance-none cursor-pointer"
                  title="Ajusta la producción promedio por hectárea"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>15,000 kg</span>
                  <span>35,000 kg</span>
                </div>
              </div>

              {/* Price per Kg */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700">Precio Promedio de Venta por Kg</label>
                <div className="flex items-center justify-center gap-4 mb-3">
                  <button 
                    onClick={() => updateInvestment({ averageSalePricePerKg: Math.max(20, investment.averageSalePricePerKg - 1) })}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.averageSalePricePerKg <= 20}
                  >
                    <Minus className="w-5 h-5 text-gray-700" />
                  </button>
                  <div className="text-center">
                    <input
                      type="number"
                      min="20"
                      max="50"
                      step="1"
                      value={investment.averageSalePricePerKg}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value >= 20 && value <= 50) {
                          updateInvestment({ averageSalePricePerKg: value });
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-20 text-center text-2xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                    />
                    <div className="text-sm text-blue-600">
                      MXN/kg
                    </div>
                  </div>
                  <button 
                    onClick={() => updateInvestment({ averageSalePricePerKg: Math.min(50, investment.averageSalePricePerKg + 1) })}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.averageSalePricePerKg >= 50}
                  >
                    <Plus className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
                
                <input
                  type="range"
                  min="20"
                  max="50"
                  step="1"
                  value={investment.averageSalePricePerKg}
                  onChange={(e) => updateInvestment({ averageSalePricePerKg: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg appearance-none cursor-pointer"
                  title="Ajusta el precio promedio de venta por kg"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>$20 MXN</span>
                  <span>$50 MXN</span>
                </div>
              </div>
            </div>

            {/* Calculated Annual Profit Display */}
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Utilidad anual calculada:</div>
              <div className="text-2xl font-bold text-green-700 relative overflow-hidden">
                <AnimatedNumberDisplay
                  value={((investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65 / investment.certificateBasePrice) * 100)}
                  currencyFormat="MXN"
                  duration={1.2}
                  staggerDelay={0.08}
                  isCurrency={false}
                />%
              </div>
              <div className="text-xs text-green-600 mt-1">
                {investment.averageProductionPerHectare.toLocaleString()} kg/ha × ${investment.averageSalePricePerKg}/kg × 0.1 × 0.65
              </div>
            </div>

            {/* Scenario Presets */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Conservador', production: 25000, price: 30 },
                { label: 'Moderado', production: 30000, price: 35 },
                { label: 'Optimista', production: 35000, price: 38 }
              ].map((scenario) => (
                <button
                  key={scenario.label}
                  onClick={() => updateInvestment({ 
                    averageProductionPerHectare: scenario.production,
                    averageSalePricePerKg: scenario.price
                  })}
                  className={`py-4 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                    investment.averageProductionPerHectare === scenario.production && investment.averageSalePricePerKg === scenario.price
                      ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={`Escenario ${scenario.label}: ${scenario.production.toLocaleString()} kg/ha × $${scenario.price}/kg = ${((scenario.production * scenario.price * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}% utilidad anual`}
                >
                  {scenario.label}
                </button>
              ))}
            </div>
          </div>
          
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" />
              <span>Configura tu Meta</span>
            </h3>
            
            {/* Monthly Goal Configuration */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Meta de Ingreso Mensual</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Presets</span>
                  <Toggle 
                    checked={isCustomGoal}
                    onChange={() => setIsCustomGoal(!isCustomGoal)}
                    label="Personalizado"
                  />
                </div>
              </div>
              
              {isCustomGoal ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={config.monthlyGoal}
                      onChange={(e) => handleMonthlyGoalChange(Math.max(0, parseInt(e.target.value) || 0))}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Ingresa tu meta mensual"
                    />
                    <span className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                      {investment.currencyFormat}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Ingresa el monto mensual que deseas recibir al final del plazo
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {monthlyGoalPresets.map((preset, index) => (
                    <button
                      key={index}
                      onClick={() => handleMonthlyGoalChange(preset.value)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        config.monthlyGoal === preset.value
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <p className="font-medium mb-1">{preset.label}</p>
                        <p className="text-lg font-bold">
                          {formatCurrency(preset.value, investment.currencyFormat)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">mensual</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Timeframe Configuration */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Plazo de Inversión</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Presets</span>
                  <Toggle 
                    checked={isCustomTimeframe}
                    onChange={() => setIsCustomTimeframe(!isCustomTimeframe)}
                    label="Personalizado"
                  />
                </div>
              </div>
              
              {isCustomTimeframe ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="5"
                      max="30"
                      value={config.timeframe}
                      onChange={(e) => handleTimeframeChange(Math.max(5, Math.min(30, parseInt(e.target.value) || 5)))}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Años (5-30)"
                    />
                    <span className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                      años
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Ingresa el número de años en que deseas alcanzar tu meta (entre 5 y 30)
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {timeframePresets.map((preset, index) => (
                    <button
                      key={index}
                      onClick={() => handleTimeframeChange(preset.value)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        config.timeframe === preset.value
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <p className="font-medium mb-1">{preset.label}</p>
                        <p className="text-lg font-bold">{preset.value}</p>
                        <p className="text-xs text-gray-500 mt-1">años</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Reinvestment Toggle */}
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 border border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <RefreshCw className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Interés Compuesto Multiplicador™</h3>
                      <p className="text-sm text-green-600">Multiplica activos reales, no solo dinero</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${(config.reinvestProfits !== undefined ? config.reinvestProfits : investment.reinvestProfits) ? 'text-green-700' : 'text-gray-500'}`}>
                      {(config.reinvestProfits !== undefined ? config.reinvestProfits : investment.reinvestProfits) ? 'ACTIVADO' : 'DESACTIVADO'}
                    </span>
                    <button
                      onClick={() => onConfigUpdate({ reinvestProfits: !(config.reinvestProfits !== undefined ? config.reinvestProfits : investment.reinvestProfits) })}
                      className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-300 ${
                        (config.reinvestProfits !== undefined ? config.reinvestProfits : investment.reinvestProfits) ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                          (config.reinvestProfits !== undefined ? config.reinvestProfits : investment.reinvestProfits) ? 'translate-x-9' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                
                {/* Explicación del Multiplicador™ con números reales */}
                <div className="space-y-6">
                  <div className="text-center mb-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">¿Cómo Funciona el Multiplicador™?</h4>
                    <p className="text-sm text-gray-600">
                      Comparación con números reales basados en tus parámetros actuales
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Inversión Tradicional - SIEMPRE sin reinversión */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center">
                          <BarChart3 className="w-5 h-5 text-gray-600" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-700">Inversión Tradicional</h4>
                      </div>
                      <div className="space-y-3 text-center">
                        <div>
                          <div className="text-sm text-gray-600">Certificados que compras:</div>
                          <div className="text-2xl font-bold text-gray-700">{config.certificatesNeeded}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Certificados que tendrás:</div>
                          <div className="text-2xl font-bold text-gray-700">{config.certificatesNeeded}</div>
                        </div>
                        <div className="bg-red-50 p-3 rounded-lg">
                          <div className="text-sm font-medium text-red-700">Resultado:</div>
                          <div className="text-lg font-bold text-red-700">Los mismos certificados</div>
                        </div>
                      </div>
                    </div>

                    {/* Con Multiplicador™ - SIEMPRE con reinversión */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-6 border-2 border-green-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-green-200 rounded-xl flex items-center justify-center">
                          <Zap className="w-5 h-5 text-green-700" />
                        </div>
                        <h4 className="text-lg font-semibold text-green-800">Con Multiplicador™ Activado</h4>
                      </div>
                      <div className="space-y-3 text-center">
                        <div>
                          <div className="text-sm text-green-600">Certificados que compras:</div>
                          <div className="text-2xl font-bold text-green-700">{config.certificatesNeeded}</div>
                        </div>
                        <div>
                          <div className="text-sm text-green-600">Certificados que tendrás:</div>
                          <div className="text-2xl font-bold text-green-700">
                            {actualResults?.certificatesSummary?.totalCertificates || config.certificatesNeeded}
                          </div>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <div className="text-sm font-medium text-green-700">Resultado:</div>
                          <div className="text-lg font-bold text-green-700">
                            ¡+{actualResults?.certificatesSummary?.fromReinvestment || 0} certificados GRATIS!
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Resumen con números reales */}
                  <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl p-6 border border-green-300">
                    <div className="text-center">
                      <h4 className="font-bold text-green-800 mb-3">🎯 Tu Ejemplo Real</h4>
                      <p className="text-lg text-green-700 mb-2">
                        <strong>Pagas por {config.certificatesNeeded} certificado{config.certificatesNeeded > 1 ? 's' : ''}</strong>
                      </p>
                      <p className="text-lg text-green-700 mb-2">
                        <strong>El Multiplicador™ te da +{actualResults?.certificatesSummary?.fromReinvestment || 0} certificados GRATIS</strong>
                      </p>
                      <p className="text-xl font-bold text-green-800">
                        <strong>Total: {actualResults?.certificatesSummary?.totalCertificates || config.certificatesNeeded} fuentes de ingresos permanentes</strong>
                      </p>
                      <div className="mt-3 text-sm text-green-600">
                        Ingreso mensual final: {formatCurrency(actualResults?.finalMonthlyIncome || 0, investment.currencyFormat)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Results Preview */}
          <motion.div 
            className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <h3 className="text-lg font-semibold text-indigo-800 mb-4">Resumen de tu Plan</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="w-5 h-5 text-indigo-600" />
                  <h4 className="font-medium text-gray-800">Meta Mensual</h4>
                </div>
                <p className="text-2xl font-bold text-indigo-700 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={config.monthlyGoal}
                    currencyFormat={investment.currencyFormat}
                    duration={1.2}
                    staggerDelay={0.08}
                  />
                </p>
                <p className="text-xs text-gray-500 mt-1">ingreso pasivo mensual</p>
              </div>
              
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <h4 className="font-medium text-gray-800">Plazo</h4>
                </div>
                <p className="text-2xl font-bold text-purple-700 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={config.timeframe}
                    currencyFormat="MXN"
                    duration={1.0}
                    staggerDelay={0.06}
                    isCurrency={false}
                  />
                </p>
                <p className="text-xs text-gray-500 mt-1">años</p>
              </div>
              
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <Target className="w-5 h-5 text-green-600" />
                  <h4 className="font-medium text-gray-800">Certificados</h4>
                </div>
                <p className="text-2xl font-bold text-green-700 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={config.certificatesNeeded}
                    currencyFormat="MXN"
                    duration={1.2}
                    staggerDelay={0.06}
                    isCurrency={false}
                  />
                </p>
                <p className="text-xs text-gray-500 mt-1">certificados necesarios</p>
              </div>
            </div>
            
            <div className="mt-6 bg-white p-4 rounded-xl shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-800">Plan de Inversión</h4>
                <p className="text-xl font-bold text-indigo-700 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={totalInvestment}
                    currencyFormat={investment.currencyFormat}
                    duration={1.4}
                    staggerDelay={0.1}
                  />
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Tú pagas por {config.certificatesNeeded} certificado{config.certificatesNeeded > 1 ? 's' : ''}:</span>
                  <div className="relative overflow-hidden font-bold text-blue-700">
                    <AnimatedNumberDisplay
                      value={totalInvestment}
                      currencyFormat={investment.currencyFormat}
                      duration={1.0}
                      staggerDelay={0.06}
                    />
                  </div>
                </div>
                <div className="flex justify-between text-sm text-green-600">
                  <span>El Multiplicador™ te da GRATIS:</span>
                  <span className="font-bold text-green-700">
                    +{withMultiplierCertificatesFromReinvestment} certificados adicionales
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <div className="flex justify-between text-base font-bold">
                    <span className="text-gray-800">Total que tendrás:</span>
                    <span className="text-green-700">{withMultiplierTotalCertificates} certificados productivos</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg border border-green-300">
              <div className="text-center">
                <h4 className="font-bold text-green-800 mb-2">🎯 Resumen Simple</h4>
                <p className="text-sm text-green-700">
                  <strong>Pagas {config.certificatesNeeded} certificado{config.certificatesNeeded > 1 ? 's' : ''}</strong> → 
                  <strong> Obtienes {withMultiplierTotalCertificates} certificados trabajando para ti</strong> → 
                  <strong> Alcanzas tu meta de {formatCurrency(config.monthlyGoal, investment.currencyFormat)} mensuales</strong>
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* Continue Button */}
          <motion.div 
            className="flex justify-end"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                window.scrollTo(0, 0);
                onContinue();
              }}
              className="px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>Ver Multiplicador™ en Acción</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default DreamConfiguration;